/* ──────────────────────────────────────────────
 *  Booking callbacks factory
 *
 *  Wires the BookingTunnel callbacks to the public
 *  booking API. Single source of truth shared by:
 *   - the website-builder BookingWidget (custom-websites)
 *   - the standalone /book/[slug] page (frontend-web)
 * ────────────────────────────────────────────── */

import {
  ProPaymentStatus,
  WebsiteContactSubmissionKind,
  type ContactSubmissionAnswerObject,
  type PreReservationResponseObject,
  type PublicBookingSource,
} from '@cfixe-monorepo/shared';
import {
  PublicApiError,
  applyCodeToPreReservation,
  applyVoucherToPreReservation,
  applyPromoToPreReservation,
  cancelPreReservation,
  checkBookingBookability,
  checkBookingEligibleMembers,
  confirmPayOnSitePreReservation,
  confirmPreReservation,
  createPreReservation,
  fetchBookingPrestations,
  fetchBookingSlots,
  fetchBookingStatus,
  fetchCartSummary,
  fetchNextBookableSlot,
  fetchProPaymentDetails,
  revealProtectedBindings,
  submitContactRequest,
} from '../../website/api/professional-api';
import { getRequiredCaptchaToken, type CapRef } from '../../website/functions/captcha';
import { DEFAULT_LABELS } from '../types';
import type {
  BookableNextSlot,
  BookableSlot,
  BookingCallbacks,
  BookingQuoteRequest,
  BookingDiscountSource,
  BookingPrestation,
  CartSummary,
  CodeResponse,
  PreReservationResponse,
  PrestationBookability,
  VoucherResponse,
  PromoResponse,
} from '../types';
import { mapPublicCustomField, serializeCustomFieldAnswers } from '../utils/custom-fields';
import {
  clearBookingTrackingReserve,
  saveBookingTrackingReserve,
  takeBookingTrackingReserve,
  type BookingTrackingEvent,
} from './booking-tracking-reserve';

type PrestationsResult = Awaited<ReturnType<BookingCallbacks['onLoadPrestations']>>;

export interface CreateBookingCallbacksOptions {
  /** Invoked after a reservation is confirmed — e.g. analytics conversion tracking */
  onReservationConfirmed?: (uuid: string) => void;
  /**
   * Événement de suivi `booking_confirmed` de la réservation confirmée, repris de la réserve
   * `sessionStorage` (cf. booking-tracking-reserve.ts). N'est PAS appelé quand la réserve est
   * absente (confirmation rechargée, paiement terminé dans un autre navigateur).
   */
  onBookingTrackingEvent?: (event: BookingTrackingEvent) => void;
  /**
   * Canal d'entrée déclaré au backend (`booking_source`, statistiques de provenance) :
   * WEBSITE sur le site du pro, WIDGET en embarqué, valeur résolue par `/book` (lien direct,
   * fiche Google, Réserver avec Google, campagne). Absent → le backend enregistre ONLINE.
   */
  source?: PublicBookingSource;
  /**
   * Campagne email / SMS à l'origine de la visite (`?cid=` d'un lien de campagne, ou référence
   * mémorisée sur le site du pro). Le backend ne la retient que si elle est bien une campagne
   * envoyée de ce pro (attribution des réservations, statistiques « Campagnes »).
   */
  campaign_id?: number;
}

/**
 * Origine de `payment_infos.discount_amount` : la carte cadeau d'abord (un code promo peut s'y ajouter),
 * seule à être un coupon que la timeline de paiement annonce restituable.
 */
function discountSourceOf(data: PreReservationResponseObject): BookingDiscountSource | undefined {
  if (data.voucher_discount != null) return 'voucher';
  if (data.promo_discount != null) return 'promo';
  return undefined;
}

/**
 * Demande de devis du tunnel en réponses du formulaire de contact : la prestation en tête (l'email du pro
 * l'affiche en premier), puis les coordonnées et le message. Clés connues du backend (`name`, `email`,
 * `phone`, `message`) pour la fiche du lead.
 */
export function quoteRequestAnswers(request: BookingQuoteRequest): ContactSubmissionAnswerObject[] {
  const answers: ContactSubmissionAnswerObject[] = [
    { key: 'prestation', label: DEFAULT_LABELS.quote_field_prestation, type: 'TEXT', value: request.prestation_name },
    {
      key: 'name',
      label: `${DEFAULT_LABELS.quote_field_firstname} ${DEFAULT_LABELS.quote_field_lastname}`,
      type: 'TEXT',
      value: `${request.firstname} ${request.lastname}`.trim(),
    },
    { key: 'email', label: DEFAULT_LABELS.quote_field_email, type: 'EMAIL', value: request.email },
    { key: 'phone', label: DEFAULT_LABELS.quote_field_phone, type: 'PHONE', value: request.phone },
    { key: 'message', label: DEFAULT_LABELS.quote_field_message, type: 'TEXTAREA', value: request.message },
  ];
  return answers.filter((answer): boolean => answer.value.length > 0);
}

/** Map the backend pre-reservation payload to the tunnel's PreReservationResponse shape */
function mapPreReservationResponse(data: PreReservationResponseObject): PreReservationResponse {
  return {
    reservation_uuid: data.reservation_uuid,
    payment_type: data.payment_type,
    expires_at: data.expires_at,
    cancellation_deadline: data.cancellation_deadline,
    cancellation_deadline_formatted: data.cancellation_deadline_formatted,
    voucher_refundable_on_cancel: data.voucher_refundable_on_cancel,
    discount_source: discountSourceOf(data),
    promo_code: data.promo_code,
    promo_code_label: data.promo_code_label,
    promotion_rejected_reason: data.promotion_rejected_reason,
    promotion_rejected_label: data.promotion_rejected_label,
    voucher_code: data.voucher_code,
    amounts: data.amounts,
    payment_infos: data.payment_infos
      ? {
          total_amount: data.payment_infos.total_amount,
          total_amount_raw: data.payment_infos.total_amount_raw,
          amount_to_pay_today: data.payment_infos.amount_to_pay_today,
          amount_to_pay_today_raw: data.payment_infos.amount_to_pay_today_raw,
          remaining_amount_on_site: data.payment_infos.remaining_amount_on_site,
          deposit_amount: data.payment_infos.deposit_amount,
          // Carte cadeau d'abord (cf. discountSourceOf) ; le récap lit chaque remise dans `amounts`.
          discount_amount: data.voucher_discount ?? data.promo_discount,
          total_after_discount: data.payment_infos.total_after_discount,
          possible_refund_in_time: data.payment_infos.possible_refund_in_time,
          possible_refund_in_time_raw: data.payment_infos.possible_refund_in_time_raw,
          possible_refund_out_time: data.payment_infos.possible_refund_out_time,
          possible_refund_out_time_raw: data.payment_infos.possible_refund_out_time_raw,
          cancellation_fee_in_time: data.payment_infos.cancellation_fee_in_time,
          cancellation_fee_in_time_raw: data.payment_infos.cancellation_fee_in_time_raw,
          cancellation_fee_out_time: data.payment_infos.cancellation_fee_out_time,
          cancellation_fee_out_time_raw: data.payment_infos.cancellation_fee_out_time_raw,
        }
      : undefined,
  };
}

/**
 * Build the full set of BookingTunnel callbacks for a given professional slug.
 * The returned object can be passed straight to `<BookingTunnel callbacks={...} />`.
 */
export function createBookingCallbacks(
  professionalSlug: string,
  options?: CreateBookingCallbacksOptions,
): BookingCallbacks {
  /* Pre-reservation uuid already reported to onReservationConfirmed — the payment status
     poll is repetitive, the hook must fire exactly once per booking. */
  let confirmedNotifiedUuid: string | undefined;
  const paymentCaptchaRef: CapRef = { current: null };
  /* Demande de devis et numéro du pro : endpoints publics sous captcha (même file que le paiement). */
  const contactCaptchaRef: CapRef = { current: null };

  /* La réserve de suivi n'est tenue que si un consommateur l'écoute. */
  const tracksBooking = options?.onBookingTrackingEvent !== undefined;

  /**
   * @param trackedAmountPaidRaw montant débité en ligne à reporter dans l'événement de suivi
   *   quand le chemin de confirmation le connaît mieux que la pré-réservation.
   */
  const notifyReservationConfirmed = (uuid: string, trackedAmountPaidRaw?: number): void => {
    if (confirmedNotifiedUuid === uuid) return;
    confirmedNotifiedUuid = uuid;
    options?.onReservationConfirmed?.(uuid);
    if (!tracksBooking) return;
    const event = takeBookingTrackingReserve(uuid, trackedAmountPaidRaw);
    if (event) options?.onBookingTrackingEvent?.(event);
  };

  return {
    onLoadPrestations: async (params): Promise<PrestationsResult> => {
      const data = await fetchBookingPrestations(professionalSlug, params);
      return {
        categories: data.categories,
        prestations: data.prestations.map((p): BookingPrestation => ({ ...p, prices: p.prices ?? [] })),
        team_members: data.team_members,
        pro_places: data.pro_places,
        custom_fields: data.custom_fields?.map(mapPublicCustomField),
        booking_params: { ...data.booking_params, allowed_payment_methods: data.allowed_payment_methods },
      };
    },

    onLoadSlots: async (params): Promise<BookableSlot[]> =>
      fetchBookingSlots(professionalSlug, {
        start_date: params.start_date,
        slots: params.slots,
        // Reconstruit l'objet params : sans ce forward explicite, le drapeau serait perdu.
        include_session_slots_below_party_size: params.include_session_slots_below_party_size,
      }),

    onFindNextSlot: async (params): Promise<BookableNextSlot> =>
      fetchNextBookableSlot(professionalSlug, {
        start_date: params.start_date,
        slots: params.slots,
        include_session_slots_below_party_size: params.include_session_slots_below_party_size,
      }),

    onSubmitQuoteRequest: async (request): Promise<void> => {
      const capToken = await getRequiredCaptchaToken(contactCaptchaRef);
      await submitContactRequest(
        professionalSlug,
        {
          kind: WebsiteContactSubmissionKind.CONTACT,
          answers: quoteRequestAnswers(request),
          // Aucune case de consentement dans le tunnel : la demande sert seulement à répondre au client.
          consent: false,
          source_url: typeof window !== 'undefined' ? window.location.href : undefined,
        },
        capToken,
      );
    },

    onRevealProPhone: async (): Promise<string | null> => {
      const capToken = await getRequiredCaptchaToken(contactCaptchaRef);
      const values = await revealProtectedBindings(professionalSlug, ['pro:phone'], capToken);
      return values['pro:phone'] || null;
    },

    onCheckBookability: async (params): Promise<PrestationBookability[]> => {
      const { signal, ...rest } = params;
      const data = await checkBookingBookability(professionalSlug, rest, signal ? { signal } : undefined);
      return data.prestations;
    },

    onCheckEligibleMembers: async (
      params,
    ): Promise<{ index: number; eligible_member_ids: number[]; unconstrained: boolean }[]> => {
      const { signal, ...rest } = params;
      const data = await checkBookingEligibleMembers(professionalSlug, rest, signal ? { signal } : undefined);
      return data.positions;
    },

    onComputeCart: async (items, voucherCode, promoCode, context): Promise<CartSummary> =>
      fetchCartSummary(professionalSlug, {
        items,
        voucher_code: voucherCode,
        promo_code: promoCode,
        ...(context?.code ? { code: context.code } : {}),
        ...(context?.slot_start ? { slot_start: context.slot_start } : {}),
        ...(context?.email ? { email: context.email } : {}),
        ...(context?.phone ? { phone: context.phone } : {}),
        ...(context?.pro_place_id ? { pro_place_id: context.pro_place_id } : {}),
      }),

    onPreReserve: async (params): Promise<PreReservationResponse> => {
      const { custom_field_answers, ...rest } = params;
      // Valeurs sérialisées en chaîne (colonne `text`) — convention par type : serializeCustomFieldAnswers.
      const backendPayload = {
        ...rest,
        custom_field_answers: custom_field_answers ? serializeCustomFieldAnswers(custom_field_answers) : undefined,
        ...(options?.source ? { source: options.source } : {}),
        ...(options?.campaign_id ? { campaign_id: options.campaign_id } : {}),
      };
      const data = await createPreReservation(professionalSlug, backendPayload);
      if (tracksBooking) saveBookingTrackingReserve(data, { customer: params.customer });
      return mapPreReservationResponse(data);
    },

    onApplyCode: async (uuid, code): Promise<CodeResponse> => {
      try {
        const data = await applyCodeToPreReservation(uuid, code);
        if (tracksBooking) saveBookingTrackingReserve(data, { previousUuid: uuid });
        return { valid: true, updated_pre_reservation: mapPreReservationResponse(data) };
      } catch (error: unknown) {
        if (error instanceof PublicApiError) {
          return { valid: false, error_key: error.errorKey, message: error.userMessage };
        }
        throw error;
      }
    },

    onApplyVoucher: async (uuid, code): Promise<VoucherResponse> => {
      try {
        const data = await applyVoucherToPreReservation(uuid, code);
        if (tracksBooking) saveBookingTrackingReserve(data, { previousUuid: uuid });
        return {
          valid: true,
          discount_amount: data.voucher_discount,
          new_total: data.payment_infos.total_amount,
          new_amount_to_pay: data.payment_infos.amount_to_pay_today,
          updated_pre_reservation: mapPreReservationResponse(data),
        };
      } catch (error: unknown) {
        if (error instanceof PublicApiError) {
          return {
            valid: false,
            error_key: error.errorKey,
            // Le tunnel n'essaie le code promo qu'après un 400 sans clé métier, pas après un 5xx / 429.
            status: error.status,
            message: error.userMessage,
          };
        }

        throw error;
      }
    },

    onRemoveVoucher: async (uuid): Promise<VoucherResponse> => {
      const data = await applyVoucherToPreReservation(uuid, undefined);
      if (tracksBooking) saveBookingTrackingReserve(data, { previousUuid: uuid });
      return {
        valid: true,
        new_total: data.payment_infos.total_amount,
        new_amount_to_pay: data.payment_infos.amount_to_pay_today,
        updated_pre_reservation: mapPreReservationResponse(data),
      };
    },

    onApplyPromo: async (uuid, code): Promise<PromoResponse> => {
      try {
        const data = await applyPromoToPreReservation(uuid, code);
        // Même suivi que la pose d'un bon : la valeur de conversion suit le total net remisé.
        if (tracksBooking) saveBookingTrackingReserve(data, { previousUuid: uuid });
        return {
          valid: true,
          discount_amount: data.promo_discount,
          new_total: data.payment_infos.total_amount,
          new_amount_to_pay: data.payment_infos.amount_to_pay_today,
          updated_pre_reservation: mapPreReservationResponse(data),
        };
      } catch (error: unknown) {
        if (error instanceof PublicApiError) {
          return { valid: false, error_key: error.errorKey, message: error.userMessage };
        }
        throw error;
      }
    },

    onRemovePromo: async (uuid): Promise<PromoResponse> => {
      const data = await applyPromoToPreReservation(uuid, undefined);
      if (tracksBooking) saveBookingTrackingReserve(data, { previousUuid: uuid });
      return {
        valid: true,
        new_total: data.payment_infos.total_amount,
        new_amount_to_pay: data.payment_infos.amount_to_pay_today,
        updated_pre_reservation: mapPreReservationResponse(data),
      };
    },

    onConfirmReservation: async (uuid): Promise<{ success: boolean; message?: string }> => {
      await confirmPreReservation(uuid);
      notifyReservationConfirmed(uuid);
      return { success: true };
    },

    onConfirmPayOnSiteReservation: async (uuid): Promise<{ success: boolean; message?: string }> => {
      await confirmPayOnSitePreReservation(uuid);
      notifyReservationConfirmed(uuid, 0);
      return { success: true };
    },

    onCancelPreReservation: async (uuid): Promise<void> => {
      await cancelPreReservation(uuid);
      if (tracksBooking) clearBookingTrackingReserve(uuid);
    },

    onLoadPaymentDetails: async (
      uuid,
    ): Promise<{ client_secret?: string; stripe_connect_account_id: string; payment_method_types?: string[] }> => {
      const capToken = await getRequiredCaptchaToken(paymentCaptchaRef);
      const data = await fetchProPaymentDetails(uuid, { headers: { 'x-cap-token': capToken } });
      return {
        client_secret: data.client_secret,
        stripe_connect_account_id: data.stripe_connect_account_id,
        payment_method_types: data.payment_method_types,
      };
    },

    onPollPaymentStatus: async (uuid): Promise<{ status: string; reservation_uuid?: string }> => {
      const data = await fetchBookingStatus(uuid);
      /* Le paiement en ligne ne passe pas par onConfirmReservation (la machine reçoit
         PAYMENT_CONFIRMED via ce poll) : c'est donc ici que le hook de confirmation
         doit être déclenché, à la première réponse « réglée ». */
      if (data.status === ProPaymentStatus.PAYED || data.status === ProPaymentStatus.AUTHORIZED) {
        // Carte seulement enregistrée ou autorisée : rien n'a été débité.
        notifyReservationConfirmed(uuid, data.status === ProPaymentStatus.AUTHORIZED ? 0 : undefined);
      }
      return { status: data.status, reservation_uuid: data.reservation_uuid };
    },
  };
}
